/**
 * Finding and measuring the DOM elements a tour points at.
 *
 * Anchors appear only after a page finishes loading its data, so the tour polls for
 * them rather than assuming they exist the moment a route changes.
 */

const POLL_MS = 60;

export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function waitForSelector(
  selector: string,
  // Real phones need this much: 2500 timed out before charts had rendered, and a
  // missed anchor drops the card to the middle of the screen with no arrow.
  timeoutMs = 6000,
): Promise<HTMLElement | null> {
  const existing = document.querySelector<HTMLElement>(selector);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const id = setInterval(() => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        clearInterval(id);
        resolve(el);
      } else if (Date.now() - startedAt > timeoutMs) {
        clearInterval(id);
        console.warn(`[tour] anchor not found: ${selector}`);
        resolve(null);
      }
    }, POLL_MS);
  });
}

export function measure(el: HTMLElement): AnchorRect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Share of the view left clear below the anchor for the card to sit in. */
const ROOM_BELOW_ANCHOR = 0.8;

export function scrollIntoView(el: HTMLElement): void {
  const anchorRect = el.getBoundingClientRect();
  const scroller = document.querySelector('[data-app-scroll]');

  // Nothing fits below an anchor this tall, so just show as much of it as possible.
  if (!scroller || anchorRect.height > window.innerHeight / 2) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    return;
  }

  // Centering left only half the view below the anchor, close enough to the minimum
  // that the same step anchored on one screen size and centred on another.
  const box = scroller.getBoundingClientRect();
  const targetBottom = box.top + box.height * (1 - ROOM_BELOW_ANCHOR);
  scroller.scrollBy({ top: anchorRect.bottom - targetBottom, behavior: 'smooth' });
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Waits for a smooth scroll to finish. Polling the anchor's position can't do this job:
 * a scroll takes a moment to start, so two early reads match and the measurement lands
 * before anything has moved.
 */
export function waitForScrollSettle(timeoutMs = 2000): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    let started = false;
    const noteStart = () => {
      started = true;
    };
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scrollend', finish, true);
      window.removeEventListener('scroll', noteStart, true);
      clearTimeout(idleTimer);
      clearTimeout(safetyTimer);
      resolve();
    };
    window.addEventListener('scroll', noteStart, { capture: true, passive: true });
    window.addEventListener('scrollend', finish, { once: true, capture: true });
    // Nothing started moving, so there is nothing to wait for.
    const idleTimer = setTimeout(() => {
      if (!started) finish();
    }, 250);
    const safetyTimer = setTimeout(finish, timeoutMs);
  });
}

/**
 * Waits until a preAction stops reflowing the page. The left nav collapsing moves
 * everything, and scrolling to where the anchor was mid-animation aims at the wrong
 * place.
 */
export async function waitForStableRect(el: HTMLElement, timeoutMs = 2000): Promise<void> {
  const snapshot = () => {
    const r = el.getBoundingClientRect();
    return `${r.top},${r.left},${r.width},${r.height}`;
  };

  let previous = snapshot();
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await sleep(80);
    const current = snapshot();
    if (current === previous) return;
    previous = current;
  }
}

/**
 * Slides a revealed element to the middle of its row. clickUntilVisible stops the moment
 * the target renders, which leaves it pinned to the edge of a windowed tab list.
 */
export async function clickUntilCentered(
  arrowSelector: string,
  oppositeSelector: string | undefined,
  selectorToCenter: string,
  maxClicks = 3,
): Promise<void> {
  const place = () => {
    const el = document.querySelector<HTMLElement>(selectorToCenter);
    const row = el?.parentElement;
    if (!el || !row) return null;
    const box = el.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    const rowCenter = rowBox.left + rowBox.width / 2;
    return {
      centered: rowCenter >= box.left && rowCenter <= box.right,
      offset: Math.abs(box.left + box.width / 2 - rowCenter),
    };
  };

  for (let i = 0; i < maxClicks; i++) {
    const before = place();
    if (!before || before.centered) return;
    const arrow = document.querySelector<HTMLButtonElement>(arrowSelector);
    if (!arrow || arrow.disabled) return;
    arrow.click();
    await sleep(120);

    // Equal offsets mean the click only moved the selection, not the window yet.
    const after = place();
    if (after && after.offset <= before.offset) continue;

    // Went the wrong way, so put the row back where it was.
    const back = oppositeSelector
      ? document.querySelector<HTMLButtonElement>(oppositeSelector)
      : null;
    if (back && !back.disabled) {
      back.click();
      await sleep(120);
    }
    return;
  }
}

/** Clicks an arrow until the element we want appears. Null if it never does. */
export async function clickUntilVisible(
  arrowSelector: string,
  selectorToReveal: string,
  maxClicks = 8,
): Promise<HTMLElement | null> {
  const alreadyVisible = document.querySelector<HTMLElement>(selectorToReveal);
  if (alreadyVisible) return alreadyVisible;

  for (let i = 0; i < maxClicks; i++) {
    const arrow = document.querySelector<HTMLButtonElement>(arrowSelector);
    // Missing, or disabled at the end of the list.
    if (!arrow || arrow.disabled) break;
    arrow.click();
    await sleep(120);
    const revealed = document.querySelector<HTMLElement>(selectorToReveal);
    if (revealed) return revealed;
  }

  console.warn(`[tour] could not reach ${selectorToReveal} using ${arrowSelector}`);
  return null;
}
